import { Injectable } from '@angular/core';

import { ErrorService } from '@services/error.service';
import { HttpService } from '@services/http.service';
import { User } from '@models/user';
import { PARTY_RELATIONSHIP_TYPES } from './constants.service';
import { PartyService } from './party.service';

@Injectable({
  providedIn: 'root'
})
export class UserService {

  constructor(
    private httpService: HttpService,
    private errorService: ErrorService,
    private partyService: PartyService
  ) { }

  public updateEnabled(
    enabled: boolean,
    username?: string,
  ): Promise<void> {
    return this.updateUser(
      username,
      undefined,
      undefined,
      undefined,
      enabled,
    );
  }

  public updateUser(username?: string, firstname?: string, lastname?: string, newPassword?: string, enabled?: boolean): Promise<void> {
    let pEnabled = null;
    if (enabled !== undefined) {
      pEnabled = enabled ? "Y" : "N";
    }

    return new Promise((resolve, reject) => {
      return this.httpService.post(`**/users/update`, { username, firstname, lastname, password: newPassword, enabled: pEnabled }).then((result: any) => {
        return resolve();
      }).catch((error: any) => {
        this.errorService.printError(error);

        return reject(error);
      })
    })
  }

  public linkUser(fromPartyId: string | undefined, toPartyId: string | undefined): Promise<void> {
    if (!fromPartyId || !toPartyId) {
      return Promise.reject();
    }

    return this.partyService.createPartyRelationship(fromPartyId, toPartyId, PARTY_RELATIONSHIP_TYPES.EMPLOYMENT);
  }

  public unlinkUser(fromPartyId: string, toPartyId: string): Promise<void> {
    if (!fromPartyId || !toPartyId) {
      return Promise.reject();
    }
    return this.partyService.removePartyRelationship(fromPartyId, toPartyId, PARTY_RELATIONSHIP_TYPES.EMPLOYMENT);
  }

  public deleteUser(username?: string): Promise<void> {
    return new Promise((resolve, reject) => {
      return this.httpService.post(`**/users/delete`, { username }).then((result: any) => {
        return resolve();
      }).catch((error: any) => {
        this.errorService.printError(error);

        return reject(error);
      })
    })
  }

  getPartyId(user: User | null): string | null {
    if (!user || !user.partyId) {
      return null;
    }

    return user.partyId;
  }

  public createUser(
    firstname: string,
    lastname: string,
    email: string,
    roles: string[],
    employerPartyId: string,
  ): Promise<User> {
    return new Promise((resolve, reject) => {
      return this.httpService.post(`**/users/create`, {
        employerPartyId,
        firstname,
        lastname,
        email,
        roles,
      }).then((user: User) => {
        return resolve(user);
      }).catch((error: any) => {
        this.errorService.printError(error);

        return reject(error);
      })
    })
  }

  public getExternalAuthId(email?: string): Promise<string | null> {
    return new Promise((resolve, reject) => {
      return this.getUserByEmail(email).then((response: User | null) => {
        if (response == null) {
          return reject('User not found');
        } else if (response.externalAuthId == undefined || response.externalAuthId == null || response.externalAuthId == '') {
          return resolve(null);
        }
        return resolve(response.externalAuthId);
      }).catch((error: any) => {
        this.errorService.printError(error);

        return reject(error);
      })
    })
  }

  public getUserByEmail(email?: string): Promise<User | null> {
    return new Promise((resolve, reject) => {
      return this.httpService.get(`**/users/get`, {
        email,
      }, {
        fullResponse: true,
      }).then((user: User | null) => {
        if (user) {
          return resolve(user);
        }
        return resolve(null);
      }).catch((error: any) => {
        if (error && error.status && error.status == 404) {
          return resolve(null);
        }

        this.errorService.printError(error);

        return reject(error);
      })
    })
  }

  public getUser(username: string): Promise<User> {
    return new Promise((resolve, reject) => {
      return this.httpService.get(`**/users/get`, { username }, {
        fullResponse: true,
      }).then((user: User) => {
        return resolve(user);
      }).catch((error: any) => {
        this.errorService.printError(error);

        return reject(error);
      })
    })
  }

  public updateEmail(
    email: string,
    username: string,
  ): Promise<User> {
    return new Promise((resolve, reject) => {
      return this.httpService.post(`**/users/change-email`, { email, username }).then((user: User) => {
        return resolve(user);
      }).catch((error: any) => {
        this.errorService.printError(error);

        return reject(error);
      })
    })
  }

  public requestEmailChange(
    email: string,
    username: string,
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      return this.httpService.post(`**/users/request-email-change`, { email, username }, undefined, 'text').then(() => {
        return resolve();
      }).catch((error: any) => {
        this.errorService.printError(error);

        return reject(error);
      })
    })
  }

}
